import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './Signup.css';
import Logo from '../components/Logo';
import LoadingSpinner from '../components/LoadingSpinner';
import { getContractApiUrl } from '../utils/apiUrls';
import { logoutForSignupSwitch, portalLoginAuthorizationParams } from '../utils/authSession';

// Backend URL configuration (Same logic as Gatekeeper)
const BACKEND_URL = getContractApiUrl();

const APPROVED_STATUSES = ['APPROVED', 'SUBSCRIBED', 'APPROVED_NON_SUBSCRIBED'];

const isTransientHttpError = (error) => {
    const status = error?.response?.status;
    return !status || status >= 500 || status === 429;
};

const Signup = ({ gatekeeperUnknown = false, gatekeeperUnavailable = false, onRetryAccess }) => {
    const { loginWithRedirect, isAuthenticated, user, logout } = useAuth0();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [checkingStatus, setCheckingStatus] = useState(true);
    const [accessDenied, setAccessDenied] = useState(false);
    const [denialReason, setDenialReason] = useState(null);
    const [accessCheckError, setAccessCheckError] = useState(null);
    const [emailInput, setEmailInput] = useState('');
    const [formError, setFormError] = useState('');
    const [retryNonce, setRetryNonce] = useState(0);

    const invite = searchParams.get('invite');
    const emailParam = searchParams.get('email');
    const hasEmailParam = Boolean(emailParam);
    const sessionEmail = user?.email?.toLowerCase().trim() || '';

    useEffect(() => {
        let cancelled = false;

        const redirectToAuth0Signup = async (email, returnTo) => {
            try {
                await loginWithRedirect({
                    authorizationParams: portalLoginAuthorizationParams({
                        screen_hint: 'signup',
                        login_hint: email,
                    }),
                    appState: { returnTo }
                });
            } catch (redirectError) {
                console.error('Auth0 redirect failed:', redirectError);
                if (!cancelled) {
                    setAccessCheckError('Could not open the login page. Please try again.');
                    setCheckingStatus(false);
                }
            }
        };

        const applyAccessStatus = async (email, returnTo = '/') => {
            const normalizedTarget = email.toLowerCase().trim();
            const response = await axios.get(
                `${BACKEND_URL}/user/access-status?email=${encodeURIComponent(normalizedTarget)}`
            );
            if (cancelled) {
                return;
            }

            const status = response.data.status;
            console.log(`Signup Access Check for ${normalizedTarget}: ${status}`);

            if (APPROVED_STATUSES.includes(status)) {
                if (isAuthenticated) {
                    if (sessionEmail === normalizedTarget) {
                        navigate(returnTo, { replace: true });
                    } else {
                        logoutForSignupSwitch(
                            logout,
                            `/signup?email=${encodeURIComponent(normalizedTarget)}`,
                        );
                    }
                    return;
                }
                await redirectToAuth0Signup(normalizedTarget, returnTo);
                return;
            }

            if (status === 'WAITLIST') {
                setDenialReason('waitlist');
            } else {
                setDenialReason('not_approved');
            }
            setAccessDenied(true);
            setCheckingStatus(false);
        };

        const checkAndRedirect = async () => {
            setAccessDenied(false);
            setDenialReason(null);
            setAccessCheckError(null);
            setFormError('');

            if (gatekeeperUnknown || gatekeeperUnavailable) {
                setCheckingStatus(false);
                return;
            }

            if (invite) {
                setCheckingStatus(true);
                try {
                    const response = await axios.get(`${BACKEND_URL}/user/invite-resolve?t=${encodeURIComponent(invite)}`);
                    const canonicalEmail = response.data.email.toLowerCase().trim();
                    if (isAuthenticated && sessionEmail && sessionEmail !== canonicalEmail) {
                        logoutForSignupSwitch(
                            logout,
                            `/signup?invite=${encodeURIComponent(invite)}`,
                        );
                        return;
                    }
                    await redirectToAuth0Signup(canonicalEmail, '/profile?onboarding=1');
                } catch (error) {
                    console.error('Invite token resolution failed:', error);
                    if (!cancelled) {
                        if (isTransientHttpError(error)) {
                            setAccessCheckError('Could not verify this invite right now. Please try again in a moment.');
                        } else {
                            setDenialReason('invite_invalid');
                            setAccessDenied(true);
                        }
                        setCheckingStatus(false);
                    }
                }
                return;
            }

            if (emailParam) {
                setCheckingStatus(true);
                try {
                    await applyAccessStatus(emailParam);
                } catch (error) {
                    console.error('Error checking access status:', error);
                    if (!cancelled) {
                        setAccessCheckError('We could not verify your email right now. Please try again in a moment.');
                        setCheckingStatus(false);
                    }
                }
                return;
            }

            if (isAuthenticated && sessionEmail) {
                setCheckingStatus(true);
                try {
                    await applyAccessStatus(sessionEmail);
                } catch (error) {
                    console.error('Error checking access status:', error);
                    if (!cancelled) {
                        setAccessCheckError('We could not verify your account right now. Please try again in a moment.');
                        setCheckingStatus(false);
                    }
                }
                return;
            }

            setCheckingStatus(false);
        };

        checkAndRedirect();
        return () => {
            cancelled = true;
        };
    }, [
        emailParam,
        gatekeeperUnknown,
        gatekeeperUnavailable,
        invite,
        isAuthenticated,
        loginWithRedirect,
        logout,
        navigate,
        retryNonce,
        sessionEmail,
    ]);

    const handleEmailSubmit = (event) => {
        event.preventDefault();
        const normalized = emailInput.trim().toLowerCase();
        if (!normalized || !normalized.includes('@')) {
            setFormError('Enter the email address from your KeyFlo welcome message.');
            return;
        }
        setFormError('');
        navigate(`/signup?email=${encodeURIComponent(normalized)}`);
    };

    const handleRetryAccessCheck = () => {
        setAccessCheckError(null);
        setRetryNonce((value) => value + 1);
    };

    const handleTryDifferentEmail = () => {
        if (isAuthenticated) {
            logoutForSignupSwitch(logout, '/signup');
            return;
        }
        navigate('/signup');
    };

    if (checkingStatus) {
        return (
            <div className="signup-container">
                <LoadingSpinner />
                <p style={{ marginTop: '20px', color: '#666' }}>Checking access...</p>
            </div>
        );
    }

    if (gatekeeperUnavailable) {
        return (
            <div className="signup-container">
                <div className="signup-content">
                    <h1 className="signup-headline">Could not verify access</h1>
                    <div style={{ margin: '10px 0 20px 0' }}>
                        <Logo size={120} />
                    </div>
                    <p className="signup-subtitle">
                        KeyFlo could not check your account just now. This is not a permissions problem.
                    </p>
                    <p className="signup-text">
                        Refresh this page, or try again in a moment.
                    </p>
                    {onRetryAccess ? (
                        <div className="signup-form">
                            <button type="button" className="signup-submit-btn" onClick={onRetryAccess}>
                                Try again
                            </button>
                        </div>
                    ) : null}
                </div>
            </div>
        );
    }

    if (gatekeeperUnknown) {
        const accountEmail = sessionEmail || 'your account';
        return (
            <div className="signup-container">
                <div className="signup-content">
                    <h1 className="signup-headline">Access not available</h1>
                    <div style={{ margin: '10px 0 20px 0' }}>
                        <Logo size={120} />
                    </div>
                    <p className="signup-subtitle">
                        {accountEmail} is not authorized for KeyFlo portal access yet.
                    </p>
                    <p className="signup-text">
                        Learn more at{' '}
                        <a href="https://keyflo.ai?utm_source=portal_signup" target="_blank" rel="noopener noreferrer" className="signup-link">
                            keyflo.ai
                        </a>
                        {' '}or contact{' '}
                        <a href="mailto:support@keyflo.ai" className="signup-link">support@keyflo.ai</a>.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="signup-container">
            <div className="signup-content">
                <h1 className="signup-headline">
                    {accessDenied
                        ? (denialReason === 'invite_invalid'
                            ? 'Invite link unavailable'
                            : denialReason === 'waitlist'
                                ? 'You are on the waitlist'
                                : 'Access not available yet')
                        : 'Create your KeyFlo account'}
                </h1>

                <div style={{ margin: '10px 0 20px 0' }}>
                    <Logo size={120} />
                </div>

                {accessDenied && denialReason === 'invite_invalid' ? (
                    <>
                        <p className="signup-subtitle">
                            This invite link is invalid, expired, or has already been used.
                        </p>
                        <p className="signup-text">
                            Ask the deal owner to send a new invite, or contact{' '}
                            <a href="mailto:support@keyflo.ai" className="signup-link">support@keyflo.ai</a>.
                        </p>
                    </>
                ) : accessDenied && denialReason === 'waitlist' ? (
                    <>
                        <p className="signup-subtitle">
                            {emailParam || sessionEmail} is on the KeyFlo waitlist.
                        </p>
                        <p className="signup-text">
                            We will email you when access opens. Learn more at{' '}
                            <a href="https://keyflo.ai?utm_source=portal_signup" target="_blank" rel="noopener noreferrer" className="signup-link">
                                keyflo.ai
                            </a>.
                        </p>
                        {!isAuthenticated ? (
                            <p className="signup-text signup-secondary-link">
                                <button type="button" className="signup-link-button" onClick={handleTryDifferentEmail}>
                                    Try a different email
                                </button>
                            </p>
                        ) : null}
                    </>
                ) : accessDenied ? (
                    <>
                        <p className="signup-subtitle">
                            {emailParam
                                ? `${emailParam} is not approved for portal signup yet.`
                                : `${sessionEmail || 'This email'} is not approved for portal signup yet.`}
                        </p>
                        <p className="signup-text">
                            Join the waitlist or learn more at{' '}
                            <a href="https://keyflo.ai?utm_source=portal_signup" target="_blank" rel="noopener noreferrer" className="signup-link">
                                keyflo.ai
                            </a>
                            .
                        </p>
                        <p className="signup-text signup-secondary-link">
                            <button type="button" className="signup-link-button" onClick={handleTryDifferentEmail}>
                                {isAuthenticated ? 'Sign out and try a different email' : 'Try a different email'}
                            </button>
                        </p>
                    </>
                ) : accessCheckError ? (
                    <>
                        <p className="signup-subtitle">{accessCheckError}</p>
                        {invite ? (
                            <p className="signup-text">We could not verify your invite link.</p>
                        ) : (
                            <p className="signup-text">
                                Checking access for{' '}
                                <strong>{emailParam || sessionEmail}</strong>.
                            </p>
                        )}
                        <div className="signup-form">
                            <button type="button" className="signup-submit-btn" onClick={handleRetryAccessCheck}>
                                Try again
                            </button>
                            {!invite ? (
                                <button type="button" className="signup-link-button" onClick={handleTryDifferentEmail}>
                                    {isAuthenticated ? 'Sign out and try a different email' : 'Use a different email'}
                                </button>
                            ) : null}
                        </div>
                    </>
                ) : !hasEmailParam && !isAuthenticated ? (
                    <>
                        <p className="signup-subtitle">
                            Enter the email address from your KeyFlo welcome email.
                        </p>
                        <form className="signup-form" onSubmit={handleEmailSubmit}>
                            <input
                                type="email"
                                className="signup-email-input"
                                value={emailInput}
                                onChange={(event) => setEmailInput(event.target.value)}
                                placeholder="you@brokerage.com"
                                autoComplete="email"
                            />
                            {formError ? <p className="signup-form-error">{formError}</p> : null}
                            <button type="submit" className="signup-submit-btn">
                                Continue
                            </button>
                        </form>
                        <p className="signup-text signup-secondary-link">
                            Already have an account?{' '}
                            <a href="/" className="signup-link">Sign in</a>
                        </p>
                    </>
                ) : null}
            </div>
        </div>
    );
};

export default Signup;
